import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  NotificationsDataService,
  PaginatedNotificationsResult,
} from '@app/account/notifications/services/notifications-data.service';
import {
  ChannelNotification,
  ChannelNotificationFilterAttributes,
  NotificationRuleSeverityEnum,
} from '@app/generated/graphql';
import {
  catchError,
  combineLatest,
  EMPTY,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import * as Sentry from '@sentry/browser';
import { MessageService } from 'primeng/api';
import { NotificationAlertFilterValue } from '@app/account/notifications/interfaces/notifications-data.interfaces';
import { NotificationActionService } from '@app/account/notifications-and-alerts/notification-action.service';

type AlertsState = {
  loading: boolean;
  data: ChannelNotification[];
  error: Error | unknown | null;
};

/**
 * Represents the optimistic adjustments to apply to each alert count.
 * Positive values increment, negative values decrement.
 */
interface IAlertsCountAdjustments {
  totalAlerts?: number;
  filteredAlerts?: number;
  unreadAlerts?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AlertsOrchestratorService {
  // Dependencies
  private _notificationDataService = inject(NotificationsDataService);
  private _messageService = inject(MessageService);
  private _notificationActionService = inject(NotificationActionService);

  readonly PAGE_SIZE = 20;

  readonly INITIAL_STATE: AlertsState = {
    loading: true,
    data: [],
    error: null,
  } as const;

  private _appliedFilters = signal<NotificationAlertFilterValue>(
    NotificationAlertFilterValue.UnreadAndRead,
  );

  /**
   * Triggers a full refresh of the current view. Used after bulk actions (e.g. mark all as read, archive all)
   * where we intentionally reset the list to page one rather than trying to maintain scroll position,
   * since the bulk operation invalidates the current pagination state.
   *
   * startWith(undefined) is required because combineLatest only emits once every source has emitted
   * at least one value. Without it, the initial fetch would never fire since nothing has triggered the subject yet.
   */
  private _refreshSubject = new Subject<void>();

  private _params$ = combineLatest({
    filterAttributes: toObservable(this._appliedFilters).pipe(
      map((filter: NotificationAlertFilterValue) =>
        this._getChannelNotificationFilterAttributes(filter),
      ),
    ),
    refresh: this._refreshSubject.asObservable().pipe(startWith(undefined)),
  });

  /** Alerts received from the real-time subscription, rendered in the headless toast component. */
  private _incomingAlerts = signal<ChannelNotification[]>([]);

  /** Alerts appended via infinite scroll pagination. */
  private _appendedAlerts = signal<ChannelNotification[]>([]);

  // Pagination
  private _endCursor = signal<string | null>(null);
  private _hasNextPage = signal<boolean>(false);
  private _loadingMore = signal<boolean>(false);

  /**
   * The cursor that STARTED the last page fetch (i.e. the endCursor of the previous page).
   * Always points to an item unaffected by mutations on the current page, so it remains
   * valid as a backfill anchor even when _endCursor becomes stale.
   */
  private _backfillCursor = signal<string | null>(null);

  // Optimistic updates
  private _optimisticChanges = signal<
    Map<string, Partial<ChannelNotification>>
  >(new Map());

  /** The IDs of the alerts that have been flagged for removal. */
  private _flaggedAlertIds = signal<Set<string>>(new Set());

  // Counts
  /** The number of filtered alerts which correlates with the totalCount variable on the getNotifications query. */
  filteredAlertsCount = signal<number>(0);
  unreadAlertsCount = signal<number>(0);
  totalAlertsCount = signal<number>(0);

  // Core state driven by params
  state = toSignal(
    this._params$.pipe(
      tap(() => this._resetLocalState()),
      switchMap(({ filterAttributes }) =>
        this._notificationDataService
          .getNotifications$(filterAttributes, this.PAGE_SIZE)
          .pipe(
            take(1),
            tap((result: PaginatedNotificationsResult) => {
              this._updatePaginationState(result.pageInfo);
              this.filteredAlertsCount.set(result.totalCount);
            }),
            map(
              (result: PaginatedNotificationsResult): AlertsState => ({
                loading: false,
                data: result.nodes,
                error: null,
              }),
            ),
            startWith(this.INITIAL_STATE),
            catchError((err: Error) =>
              of<AlertsState>({
                loading: false,
                data: [],
                error: err,
              }),
            ),
          ),
      ),
    ),
    { initialValue: this.INITIAL_STATE },
  );

  // Computed signals
  alerts = computed<ChannelNotification[]>(() => {
    const allAlerts: ChannelNotification[] = this._getAllAlerts();

    const uniqueAlerts: ChannelNotification[] =
      this._deduplicateById(allAlerts);

    const visibleAlerts: ChannelNotification[] =
      this._removeFlaggedAlerts(uniqueAlerts);

    return this._mergeOptimisticChanges(visibleAlerts);
  });

  loading = computed(() => this.state().loading);
  loadingMore = this._loadingMore.asReadonly();
  hasNextPage = this._hasNextPage.asReadonly();
  error = computed(() => this.state().error);

  constructor() {
    this._fetchAlertCounts();

    effect(() => {
      const error = this.error();
      if (error) {
        console.error('error', error);
        Sentry.captureException(error);
      }
    });
  }

  onFilterAlerts(filter: NotificationAlertFilterValue): void {
    this._appliedFilters.set(filter);
  }

  /** Adds an incoming alert from the real-time subscription to the list of alerts. */
  addIncomingAlert(alert: ChannelNotification): void {
    if (!alert?.id) return;

    // Do not show archived alerts in the alerts list
    const isArchivedView: boolean =
      this._appliedFilters() === NotificationAlertFilterValue.Archived;

    // Do not show notifications in the alerts list
    const isNotification: boolean =
      alert.notificationRule?.severity === NotificationRuleSeverityEnum.Info;

    if (isArchivedView || isNotification) return;

    // Prevent duplicates in case the getNotifications and real-time subscription happen simultaneously
    const isDuplicate: boolean = this._getAllAlerts().some(
      (n: ChannelNotification) => n.id === alert.id,
    );
    if (isDuplicate) return;

    this._incomingAlerts.update((prev: ChannelNotification[]) => [
      alert,
      ...prev,
    ]);

    this._adjustCountsOptimistically({
      totalAlerts: 1,
      filteredAlerts: 1,
      unreadAlerts: alert.readAt ? 0 : 1,
    });
  }

  /** Triggered when the user clicks on the Dismiss/Close/Got it and CTA buttons in the headless toast component. */
  onMarkIncomingAlertAsRead(alert: ChannelNotification): void {
    this.onMarkAlertAsRead(alert);
  }

  loadMore(): void {
    if (!this._hasNextPage() || this._loadingMore() || this.loading()) {
      return;
    }

    this._loadingMore.set(true);

    // Snapshot current cursor as backfill anchor before this fetch advances it.
    this._backfillCursor.set(this._endCursor());

    const filterAttributes: ChannelNotificationFilterAttributes =
      this._getChannelNotificationFilterAttributes(this._appliedFilters());

    this._notificationDataService
      .getNotifications$(filterAttributes, this.PAGE_SIZE, this._endCursor())
      .pipe(
        take(1),
        tap((result: PaginatedNotificationsResult) => {
          this._appendedAlerts.update((prev: ChannelNotification[]) => [
            ...prev,
            ...result.nodes,
          ]);
          this._updatePaginationState(result.pageInfo);
          this._loadingMore.set(false);
        }),
        catchError((err: Error) => {
          this._loadingMore.set(false);
          return this._handleError(err);
        }),
      )
      .subscribe();
  }

  onMarkAlertAsRead(alert: ChannelNotification): void {
    if (!alert?.id) return;
    if (alert?.readAt) return;

    this._applyMarkAsReadOptimisticUpdates([alert.id]);
    this._markAlertsAsRead([alert.id]);
  }

  onMarkAlertAsUnread(alert: ChannelNotification): void {
    if (!alert?.id) return;

    this._applyOptimisticUpdates([alert.id], {
      readAt: null,
      archivedAt: null,
    });
    this._adjustCountsOptimistically({ unreadAlerts: 1 });

    // If the alert is already archived, remove it from the visible list of archived alerts
    // and decrement the filtered alerts count. As archived alerts that are marked as unread
    // must be 'unarchived' as well.
    if (alert.archivedAt) {
      this._flagAlertsForRemoval([alert.id]);
      this._adjustCountsOptimistically({ filteredAlerts: -1 });
    }

    this._markAlertsAsUnread([alert.id]);
  }

  onMarkAllAsRead(ids: string[]): void {
    if (!ids?.length) return;

    this._applyMarkAsReadOptimisticUpdates(ids);
    this._markAlertsAsRead(ids, true);
  }

  onAlertClick(alert: ChannelNotification): void {
    if (!alert?.notificationRule?.notificationSlug) return;

    // Mark the alert as read if it isn't
    if (!alert.readAt) {
      this.onMarkAlertAsRead(alert);
    }

    this._notificationActionService.executeAction(
      alert.channelMetadata,
      alert.notificationRule.notificationSlug,
    );
  }

  onArchiveAlert(alert: ChannelNotification): void {
    if (!alert?.id) return;

    this._applyArchiveOptimisticUpdates([alert.id]);
    this._archiveAlerts([alert.id]);
  }

  onArchiveAllReadAlerts(ids: string[]): void {
    if (!ids?.length) return;

    this._applyArchiveOptimisticUpdates(ids);
    this._archiveAlerts(ids, true);
  }

  onArchiveAllAlerts(ids: string[]): void {
    if (!ids?.length) return;

    this._applyArchiveOptimisticUpdates(ids);
    this._archiveAlerts(ids, true);
  }

  /**
   * Marks alerts as read on the backend.
   * @param ids - The IDs of the alerts to mark as read.
   * @param refreshView - When true, refreshes the current view after the server responds.
   *   Used for bulk actions where the list and counts need to be re-synced with the server.
   *   Defaults to false for single-item actions where optimistic updates are sufficient.
   */
  private _markAlertsAsRead(ids: string[], refreshView: boolean = false): void {
    this._notificationDataService
      .markNotificationsAsRead$(ids)
      .pipe(
        take(1),
        tap(() => {
          if (refreshView) {
            this._refreshCurrentView();
          } else {
            this._backfillAlerts();
          }
        }),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  private _markAlertsAsUnread(ids: string[]): void {
    this._notificationDataService
      .markNotificationsAsUnread$(ids)
      .pipe(
        take(1),
        tap(() => this._backfillAlerts()),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  /**
   * Archives alerts on the backend.
   * @param ids - The IDs of the alerts to archive.
   * @param refreshView - When true, refreshes the current view after the server responds.
   *   Used for bulk actions where the list and counts need to be re-synced with the server.
   *   Defaults to false for single-item actions where optimistic updates are sufficient.
   */
  private _archiveAlerts(ids: string[], refreshView: boolean = false): void {
    if (!ids?.length) return;

    this._notificationDataService
      .archiveNotifications$(ids)
      .pipe(
        take(1),
        tap(() => {
          if (refreshView) {
            this._refreshCurrentView();
          } else {
            this._backfillAlerts();
          }
        }),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  /**
   * Re-fetches from _backfillCursor to fill gaps after single-item removals like archive and mark as read
   * and recalibrate _endCursor for subsequent loadMore calls.
   */
  private _backfillAlerts(): void {
    if (this._loadingMore() || this.loading()) return;

    const visibleCount: number = this.alerts().length;
    const serverCount: number = this.filteredAlertsCount();

    // All items are already loaded — nothing to backfill
    if (visibleCount >= serverCount) return;

    this._loadingMore.set(true);

    const filterAttributes: ChannelNotificationFilterAttributes =
      this._getChannelNotificationFilterAttributes(this._appliedFilters());

    this._notificationDataService
      .getNotifications$(
        filterAttributes,
        this.PAGE_SIZE,
        this._backfillCursor(),
      )
      .pipe(
        take(1),
        tap((result: PaginatedNotificationsResult) => {
          this._appendedAlerts.update((prev: ChannelNotification[]) => [
            ...prev,
            ...result.nodes,
          ]);
          this._updatePaginationState(result.pageInfo);
          this._loadingMore.set(false);
        }),
        catchError((err: Error) => {
          this._loadingMore.set(false);
          return this._handleError(err);
        }),
      )
      .subscribe();
  }

  /** Applies optimistic updates for marking as read while adjusting unread alerts count. */
  private _applyMarkAsReadOptimisticUpdates(ids: string[]): void {
    if (!ids?.length) return;

    const unreadIds: string[] = this._getUnreadIds(ids);
    const readAtTimestamp: string = new Date().toISOString();

    this._applyOptimisticUpdates(ids, { readAt: readAtTimestamp });
    this._adjustCountsOptimistically({
      unreadAlerts: -unreadIds.length,
    });

    // On the unread filter, read alerts no longer match the view
    if (this._appliedFilters() === NotificationAlertFilterValue.UnreadOnly) {
      this._flagAlertsForRemoval(unreadIds);
      this._adjustCountsOptimistically({
        filteredAlerts: -unreadIds.length,
      });
    }
  }

  /** Applies optimistic updates for archiving and flags alerts for removal while adjusting counts. */
  private _applyArchiveOptimisticUpdates(ids: string[]): void {
    if (!ids?.length) return;

    const unreadAlertIds: string[] = this._getUnreadIds(ids);
    const readAtTimestamp: string = new Date().toISOString();

    if (unreadAlertIds.length) {
      this._applyOptimisticUpdates(unreadAlertIds, {
        readAt: readAtTimestamp,
      });
    }

    this._flagAlertsForRemoval(ids);

    this._adjustCountsOptimistically({
      filteredAlerts: -ids.length,
      unreadAlerts: -unreadAlertIds.length,
    });
  }

  /** Applies optimistic changes to a list of alerts by ID. */
  private _applyOptimisticUpdates(
    ids: string[],
    changes: Partial<ChannelNotification>,
  ): void {
    this._optimisticChanges.update(
      (currentChanges: Map<string, Partial<ChannelNotification>>) => {
        const nextChanges: Map<string, Partial<ChannelNotification>> = new Map(
          currentChanges,
        );
        ids.forEach((id: string) =>
          nextChanges.set(id, { ...nextChanges.get(id), ...changes }),
        );
        return nextChanges;
      },
    );
  }

  /** Flags alerts for removal from the visible list before the server confirms the operation. */
  private _flagAlertsForRemoval(ids: string[]): void {
    this._flaggedAlertIds.update((currentIds: Set<string>) => {
      const nextIds: Set<string> = new Set(currentIds);
      ids.forEach((id: string) => nextIds.add(id));
      return nextIds;
    });
  }

  /**
   * Adjusts alert counts optimistically by the given adjustments.
   * Math.max(0, ...) is used as a safeguard to prevent counts from going negative.
   * This can happen when optimistic updates and server responses arrive out of order,
   * or when multiple rapid user actions cause overlapping adjustments.
   */
  private _adjustCountsOptimistically(
    adjustments: IAlertsCountAdjustments,
  ): void {
    if (adjustments.totalAlerts) {
      this.totalAlertsCount.update((count: number) =>
        Math.max(0, count + adjustments.totalAlerts!),
      );
    }
    if (adjustments.filteredAlerts) {
      this.filteredAlertsCount.update((count: number) =>
        Math.max(0, count + adjustments.filteredAlerts!),
      );
    }
    if (adjustments.unreadAlerts) {
      this.unreadAlertsCount.update((count: number) =>
        Math.max(0, count + adjustments.unreadAlerts!),
      );
    }
  }

  /**
   * Refreshes the current view and alert counts.
   * Used after bulk actions to ensure the list and counts are in sync with the server.
   */
  private _refreshCurrentView(): void {
    this._refreshSubject.next();
    this._fetchAlertCounts();
  }

  private _fetchAlertCounts(): void {
    this._notificationDataService
      .getTotalChannelNotificationsCount$(NotificationRuleSeverityEnum.Alert)
      .pipe(
        take(1),
        tap((count: number) => this.totalAlertsCount.set(count)),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();

    this._notificationDataService
      .getUnreadChannelNotificationsCount$(NotificationRuleSeverityEnum.Alert)
      .pipe(
        take(1),
        tap((count: number) => this.unreadAlertsCount.set(count)),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  /** Removes duplicate alerts, keeping the first occurrence of each ID. */
  private _deduplicateById(
    alerts: ChannelNotification[],
  ): ChannelNotification[] {
    const seenIds: Set<string> = new Set<string>();
    return alerts.filter((alert: ChannelNotification) => {
      if (seenIds.has(alert.id)) return false;
      seenIds.add(alert.id);
      return true;
    });
  }

  /** Filters out alerts that have been flagged for removal. */
  private _removeFlaggedAlerts(
    alerts: ChannelNotification[],
  ): ChannelNotification[] {
    const flaggedIds: Set<string> = this._flaggedAlertIds();
    return alerts.filter(
      (alert: ChannelNotification) => !flaggedIds.has(alert.id),
    );
  }

  /** Returns a new list with any pending optimistic changes merged into the corresponding alerts. */
  private _mergeOptimisticChanges(
    alerts: ChannelNotification[],
  ): ChannelNotification[] {
    const pendingChanges: Map<
      string,
      Partial<ChannelNotification>
    > = this._optimisticChanges();
    return alerts.map((alert: ChannelNotification) => {
      const changes: Partial<ChannelNotification> | undefined =
        pendingChanges.get(alert.id);
      return changes ? { ...alert, ...changes } : alert;
    });
  }

  /** Returns the raw, unprocessed list of all alerts across all sources. */
  private _getAllAlerts(): ChannelNotification[] {
    return [
      ...this._incomingAlerts(),
      ...this.state().data,
      ...this._appendedAlerts(),
    ];
  }

  private _getUnreadIds(ids: string[]): string[] {
    const allAlerts: ChannelNotification[] = this._getAllAlerts();
    const pendingChanges: Map<
      string,
      Partial<ChannelNotification>
    > = this._optimisticChanges();

    return ids.filter((id: string) => {
      const alert: ChannelNotification | undefined = allAlerts.find(
        (a: ChannelNotification) => a.id === id,
      );
      if (!alert) return false;

      // Check optimistic changes first, as the raw alert data may not yet
      // reflect prior actions (e.g. a mark-as-read that hasn't been server-confirmed).
      // Without this, sequential actions like mark asread → archive would double-decrement
      // the unread count because the raw data still shows readAt as null.
      const changes: Partial<ChannelNotification> | undefined =
        pendingChanges.get(id);
      const effectiveReadAt: string | undefined =
        changes?.readAt !== undefined ? changes.readAt : alert.readAt;

      return !effectiveReadAt;
    });
  }

  private _resetLocalState(): void {
    this._clearAccumulatedAlerts();
    this._clearPendingOptimisticUpdates();
    this._resetPaginationState();
  }

  private _clearAccumulatedAlerts(): void {
    this._incomingAlerts.set([]);
    this._appendedAlerts.set([]);
  }

  private _clearPendingOptimisticUpdates(): void {
    this._optimisticChanges.set(new Map());
    this._flaggedAlertIds.set(new Set());
  }

  private _resetPaginationState(): void {
    this._endCursor.set(null);
    this._hasNextPage.set(false);
    this._loadingMore.set(false);
    this._backfillCursor.set(null);
  }

  private _updatePaginationState(pageInfo: {
    endCursor: string | null;
    hasNextPage: boolean;
  }): void {
    this._endCursor.set(pageInfo.endCursor);
    this._hasNextPage.set(pageInfo.hasNextPage);
  }

  private _getChannelNotificationFilterAttributes(
    filter: NotificationAlertFilterValue | null,
  ): ChannelNotificationFilterAttributes {
    const baseChannelNotificationFilterAttributes: ChannelNotificationFilterAttributes =
      {
        archived: false,
        unreadAndRead: true,
        unread: false,
        // Default severity is alert which correlates with critical alerts
        severity: NotificationRuleSeverityEnum.Alert,
      };

    switch (filter) {
      case NotificationAlertFilterValue.UnreadOnly:
        return {
          ...baseChannelNotificationFilterAttributes,
          unreadAndRead: false,
          unread: true,
        };
      case NotificationAlertFilterValue.Archived:
        return {
          ...baseChannelNotificationFilterAttributes,
          unreadAndRead: false,
          archived: true,
        };
      default:
        return baseChannelNotificationFilterAttributes;
    }
  }

  private _handleError(error: Error): Observable<never> {
    console.error('Error:', error);

    Sentry.captureException(error);

    this._messageService.add({
      severity: 'error',
      summary: error.message,
    });
    return EMPTY;
  }
}
